import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, MapPin, Search, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eyebrow, Reveal } from "../components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_TYPES, slugify } from "@/lib/constants";

const EXPERIENCES = ["Menos de 1 ano", "1 a 3 anos", "3 a 5 anos", "Mais de 5 anos"];

const PERKS = [
  "Demandas de acordo com sua especialidade e região",
  "Receba oportunidades de forma organizada",
  "Sem burocracia: cadastro simples e direto",
];

const IBGE_STATES_URL = "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome";
const ibgeCitiesUrl = (uf) =>
  `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`;

const INITIAL = { name: "", phone: "", document: "", experience: "", state: "", cities: [], specialties: [] };

export const Partners = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityFilter, setCityFilter] = useState("");

  useEffect(() => {
    fetch(IBGE_STATES_URL)
      .then((r) => r.json())
      .then(setStates)
      .catch(() => toast.error("Não foi possível carregar os estados."));
  }, []);

  useEffect(() => {
    if (!form.state) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    fetch(ibgeCitiesUrl(form.state))
      .then((r) => r.json())
      .then((data) => setCities(data.map((c) => c.nome)))
      .catch(() => toast.error("Não foi possível carregar as cidades."))
      .finally(() => setLoadingCities(false));
  }, [form.state]);

  const filteredCities = useMemo(() => {
    const q = slugify(cityFilter);
    if (!q) return cities;
    return cities.filter((c) => slugify(c).includes(q));
  }, [cities, cityFilter]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const toggleIn = (key) => (value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (form.specialties.length === 0) {
      toast.error("Selecione pelo menos um serviço que você presta.");
      return;
    }
    if (!form.state || form.cities.length === 0) {
      toast.error("Selecione o estado e pelo menos uma cidade atendida.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/partner", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        document: form.document.trim(),
        experience: form.experience,
        state: form.state,
        cities: form.cities,
        specialties: form.specialties,
      });
      toast.success("Cadastro enviado!", {
        description: "Vamos analisar seu perfil e acionar você quando surgir uma demanda na sua região e especialidade.",
      });
      setForm(INITIAL);
      setCityFilter("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="parceiros"
      className="relative overflow-hidden bg-slate-100/60 py-24 lg:py-32"
      data-testid="partners-section"
    >
      <div className="glow-brand pointer-events-none absolute -right-48 top-20 h-[460px] w-[460px] rounded-full" />
      <div className="relative mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal className="lg:sticky lg:top-28">
          <Eyebrow>Rede de parceiros</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-petrol sm:text-3xl lg:text-4xl">
            É profissional e quer fazer parte <span className="text-brand">da nossa rede?</span>
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">
            Cadastre-se na rede Resolv e receba oportunidades de serviços de acordo com sua área
            de atuação e região.
          </p>
          <ul className="mt-8 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex gap-10 border-t border-slate-200 pt-8">
            <div>
              <p className="font-display text-3xl font-extrabold text-petrol">+50</p>
              <p className="mt-1 text-xs font-medium text-slate-500">profissionais na rede</p>
            </div>
            <div>
              <p className="font-display text-3xl font-extrabold text-petrol">+20</p>
              <p className="mt-1 text-xs font-medium text-slate-500">especialidades da rede</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="rounded-3xl border border-slate-200/80 bg-white p-7 shadow-[0_30px_60px_-30px_rgba(11,25,44,0.25)] sm:p-9"
            data-testid="partner-form"
          >
            <h3 className="font-display text-xl font-bold text-petrol">Cadastro de parceiro</h3>
            <p className="mt-1 text-sm text-slate-500">Leva menos de 2 minutos.</p>

            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="partner-name">Nome completo*</Label>
                <Input
                  id="partner-name"
                  data-testid="partner-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Seu nome"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-phone">Telefone / WhatsApp*</Label>
                <Input
                  id="partner-phone"
                  data-testid="partner-phone-input"
                  required
                  minLength={8}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(48) 98888-7777"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-document">CNPJ ou CPF*</Label>
                <Input
                  id="partner-document"
                  data-testid="partner-document-input"
                  required
                  minLength={5}
                  value={form.document}
                  onChange={set("document")}
                  placeholder="00.000.000/0001-00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Experiência*</Label>
                <Select required value={form.experience} onValueChange={set("experience")}>
                  <SelectTrigger data-testid="partner-experience-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>Serviços que você presta* (toque para selecionar)</Label>
                <div className="flex flex-wrap gap-2" data-testid="partner-specialties-picker">
                  {SERVICE_TYPES.map((s) => {
                    const active = form.specialties.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => toggleIn("specialties")(s)}
                        data-testid={`partner-specialty-chip-${slugify(s)}`}
                        aria-pressed={active}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-[background-color,border-color,color] duration-200 ${
                          active
                            ? "border-brand bg-brand text-white"
                            : "border-slate-300 bg-white text-slate-600 hover:border-brand hover:text-brand"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>Estado de atuação*</Label>
                <Select
                  value={form.state}
                  onValueChange={(uf) => setForm((f) => ({ ...f, state: uf, cities: [] }))}
                >
                  <SelectTrigger data-testid="partner-state-select">
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((s) => (
                      <SelectItem key={s.sigla} value={s.sigla}>
                        {s.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.state && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Cidades atendidas* (toque para selecionar)</Label>
                  {form.cities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pb-1" data-testid="partner-cities-selected">
                      {form.cities.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand"
                        >
                          <MapPin className="h-3 w-3" />
                          {c}
                          <button
                            type="button"
                            onClick={() => toggleIn("cities")(c)}
                            aria-label={`Remover ${c}`}
                            data-testid={`partner-city-remove-${slugify(c)}`}
                            className="rounded-full p-0.5 transition-colors hover:bg-brand/20"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="rounded-xl border border-slate-200">
                    <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={cityFilter}
                        onChange={(e) => setCityFilter(e.target.value)}
                        placeholder="Buscar cidade..."
                        data-testid="partner-city-filter-input"
                        className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                      />
                    </div>
                    <div className="max-h-44 overflow-y-auto p-1.5" data-testid="partner-cities-list">
                      {loadingCities ? (
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Carregando cidades...
                        </div>
                      ) : filteredCities.length === 0 ? (
                        <p className="py-6 text-center text-sm text-slate-400">
                          Nenhuma cidade encontrada.
                        </p>
                      ) : (
                        filteredCities.map((c) => {
                          const active = form.cities.includes(c);
                          return (
                            <button
                              type="button"
                              key={c}
                              onClick={() => toggleIn("cities")(c)}
                              data-testid={`partner-city-option-${slugify(c)}`}
                              aria-pressed={active}
                              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${
                                active ? "bg-brand/10 font-semibold text-brand" : "text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {c}
                              {active && <Check className="h-4 w-4" strokeWidth={3} />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="partner-submit-button"
              className="mt-7 h-12 w-full rounded-full bg-brand font-semibold text-white transition-colors hover:bg-brand-700"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Quero ser parceiro
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            <p className="mt-4 text-center text-xs text-slate-400">
              Seus dados são usados apenas para contato da equipe Resolv.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
